import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Bell, BellOff, Loader2, Send } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { currentSubscription, disablePush, enablePush, needsHomeScreen, pushPermission } from "../../lib/push";

const EVENTS = ["Your app or plan is ready", "A build hit a problem or ran out of credits", "Your custom domain is live", "A domain was paused for an unpaid monthly fee", "Your credits are running low"];
const btn = "h-10 px-4 rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-60 transition-colors";

const Status = ({ state, devices }) => {
  const text = {
    on: `On for this device${devices > 1 ? ` · ${devices} devices in total` : ""}`,
    off: "Off for this device",
    denied: "Blocked — allow notifications for Ishukart in your browser or phone settings, then come back",
    homescreen: "On iPhone, add Ishukart to your Home Screen first, then turn notifications on from there",
    unsupported: "This browser can't show notifications",
  }[state];
  return <p className={`text-sm ${state === "on" ? "text-emerald-700" : "text-slate-600"}`} data-testid="settings-notify-status">{text}</p>;
};

export const NotificationsCard = ({ Card }) => {
  const [state, setState] = useState(null);
  const [devices, setDevices] = useState(0);
  const [busy, setBusy] = useState(null);
  const load = async () => {
    const perm = pushPermission();
    if (perm === "unsupported") return setState(needsHomeScreen() ? "homescreen" : "unsupported");
    if (perm === "denied") return setState("denied");
    setState(perm === "granted" && (await currentSubscription().catch(() => null)) ? "on" : "off");
    api.pushStatus().then((r) => setDevices(r.devices)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const run = (name, fn) => async () => { setBusy(name); try { await fn(); } catch (e) { toast.error(errMsg(e, "Something went wrong")); } finally { setBusy(null); load(); } };
  const on = run("on", async () => { const r = await enablePush(); toast[r === "granted" ? "success" : "message"](r === "granted" ? "Notifications are on" : "Notifications were not allowed"); });
  const off = run("off", async () => { await disablePush(); toast.success("Notifications turned off for this device"); });
  const test = run("test", async () => { await api.pushTest(); toast.success("Test sent — it should pop up in a few seconds"); });
  const spin = (n) => busy === n && <Loader2 size={14} className="animate-spin" />;
  return (
    <Card icon={Bell} title="Notifications" desc="Get a ping on this phone or computer, even when Ishukart is closed." testid="settings-notifications">
      {state && <Status state={state} devices={devices} />}
      <ul className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-xs text-slate-500">{EVENTS.map((e) => <li key={e} className="flex gap-2"><span className="mt-1.5 w-1 h-1 rounded-full bg-sky-500 shrink-0" />{e}</li>)}</ul>
      <div className="mt-5 flex flex-wrap gap-3">
        {state === "off" && <button onClick={on} disabled={!!busy} data-testid="settings-notify-enable" className={`${btn} bg-slate-900 text-white hover:bg-slate-700`}>{spin("on") || <Bell size={14} />} Turn on notifications</button>}
        {state === "on" && <button onClick={test} disabled={!!busy} data-testid="settings-notify-test" className={`${btn} bg-sky-500 text-white hover:bg-sky-600`}>{spin("test") || <Send size={14} />} Send a test</button>}
        {state === "on" && <button onClick={off} disabled={!!busy} data-testid="settings-notify-disable" className={`${btn} border border-slate-300 text-slate-700 hover:bg-slate-100`}>{spin("off") || <BellOff size={14} />} Turn off</button>}
        {state === "homescreen" && <Link to="/get-app#iphone" data-testid="settings-notify-howto" className={`${btn} bg-slate-900 text-white hover:bg-slate-700`}>Show me how</Link>}
      </div>
    </Card>
  );
};

export default NotificationsCard;
